import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import type { GooglePlacesAddress } from '@sweetgreen/garnish';
import {
  TextField,
  theme,
  useNoticeBannersStackContext,
  VStack,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useDuplicateCustomerAddressValidation } from './Address.hooks';
import { useAddressesTelemetry } from './AddressForm.telemetry';
import type {
  AddressField,
  AddressForm as AddressFormType,
  FormProps,
  PopulateFieldsParams,
} from './AddressForm.types';
import {
  AddressType,
  DeliveryNotes,
  DeliveryPreference,
  Footer,
  Header,
} from './components/AddressForm.components';
import { AddressFormPredictions } from './components/AddressForm.components.predictions';
import { ADDRESS_TYPE, defaultAddressForm } from './constants';

export const AddressForm = ({
  address,
  visible,
  editing,
  loading,
  deleting,
  disableAddress,
  noHeader,
  submitCta,
  onDelete,
  onEdit,
  onSave,
}: FormProps) => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── State ───────────────────────────────────────────────────────────
  const [addressType, setAddressType] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [form, setForm] = useState(defaultAddressForm as AddressFormType);
  const [isValidStreet, setIsValidStreet] = useState(false);
  const [isAddressTouched, setIsAddressTouched] = useState(false);
  const [isCustomNameTouched, setIsCustomNameTouched] = useState(false);
  const [canSubmit, setCanSubmit] = useState(editing);
  const [formChanged, setFormChanged] = useState(false);

  // ─── Address Duplication Checks ──────────────────────────────────────────
  const { checkIfDuplicateAddressByStreet, checkIfDuplicateAddressByName } =
    useDuplicateCustomerAddressValidation(address?.id);
  const isPlaceIdDuplicate = checkIfDuplicateAddressByStreet(form);
  const placeIdDuplicateNotice = t('account.address.street-duplicate');
  const isNameDuplicate =
    isCustomNameTouched && checkIfDuplicateAddressByName(form.name);
  const isNameMissing = isCustomNameTouched && !form.name;
  const isNameInvalid = isNameDuplicate ?? isNameMissing;
  const customNameNotice = useCustomNameNotice(isNameDuplicate, isNameMissing);

  // ─── Telemetry ───────────────────────────────────────────────────────────
  const { monitoredOnSave, monitoredOnEdit, monitoredOnDelete } =
    useAddressesTelemetry({
      editing,
      onDelete,
      onEdit,
      onSave,
    });

  // ─── EFFECTS ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!address || !form) return;
    const isChanged = wasAddressChanged(
      form,
      address as unknown as AddressFormType,
    );

    if (isChanged !== formChanged) setFormChanged(isChanged);
  }, [address, form, formChanged]);

  useEffect(() => {
    const hasValidAddressName =
      addressType !== ADDRESS_TYPE.CUSTOM || Boolean(form.name);

    setCanSubmit(Boolean(addressType) && isValidStreet && hasValidAddressName);
  }, [isValidStreet, addressType, form.name, editing]);

  // ───────────────────────────────────────────────────────────

  usePopulateFields({
    visible,
    address,
    placeId,
    isAddressTouched,
    editing,
    setAddressType,
    setIsValidStreet,
    setForm,
  });

  // ─── CALLBACKS ────────────────────────────────────────────────────────

  const updateForm = useCallback(
    (field: AddressField, value: string | number | undefined) => {
      setForm({ ...form, [field]: value });
    },
    [form, setForm],
  );
  const updateField =
    (field: AddressField) => (value: string | number | undefined) => {
      updateForm(field, value);
    };

  const submitForm = useCallback(() => {
    (() => {
      if (isPlaceIdDuplicate) {
        addNoticeBanner({ text: placeIdDuplicateNotice, palette: 'caution' });

        return;
      }

      if (editing) {
        monitoredOnEdit(form);

        return;
      }

      monitoredOnSave(form);
    })();
  }, [
    editing,
    form,
    isPlaceIdDuplicate,
    placeIdDuplicateNotice,
    addNoticeBanner,
    monitoredOnEdit,
    monitoredOnSave,
  ]);

  // ─────────────────────────────────────────────────────────────────

  const preventSubmittingForUnchangedForm = Boolean(address) && !formChanged;
  const preventSubmittingForInvalidName = isNameInvalid;
  const canSubmitAfterValidation =
    canSubmit &&
    !preventSubmittingForUnchangedForm &&
    !preventSubmittingForInvalidName;

  return (
    <>
      {noHeader ? (
        <View style={styles.headerPadding} />
      ) : (
        <View style={styles.header}>
          <Header editing={editing} />
        </View>
      )}

      <ScrollView
        testID="address-form.content"
        contentContainerStyle={styles.scrollContainer}
      >
        <View style={styles.body}>
          <AddressFormPredictions
            placeId={placeId}
            address={address as GooglePlacesAddress}
            visible={visible}
            disabled={disableAddress}
            setPlaceId={setPlaceId}
            setIsAddressTouched={setIsAddressTouched}
            setForm={setForm}
          />

          <VStack customSpacing={10} style={styles.content}>
            <TextField
              testID="account.address-form.secondary-street"
              accessibilityHint=""
              accessibilityLabel={`${t(
                'account.addresses.secondary-street',
              )} input`}
              label={t('account.addresses.secondary-street')}
              value={form.secondaryStreet}
              onChangeText={updateField('secondaryStreet')}
            />
            <DeliveryPreference form={form} updateField={updateField} />
            <DeliveryNotes
              form={form}
              updateField={updateField}
              maxLength={128}
            />
            <AddressType
              addressType={addressType}
              updateField={updateField}
              setAddressType={setAddressType}
            />
            {addressType === ADDRESS_TYPE.CUSTOM && (
              <TextField
                testID="account.address-form.custom-name"
                maxLength={20}
                value={form.name}
                accessibilityLabel={t('account.addresses.custom-name')}
                accessibilityHint={t('account.addresses.custom-name')}
                label={t('account.addresses.custom-name')}
                onChangeText={(text) => {
                  setIsCustomNameTouched(true);
                  updateField('name')(text);
                }}
                invalid={isNameInvalid}
                notice={customNameNotice}
              />
            )}
          </VStack>
        </View>

        <Footer
          submitCta={submitCta}
          editing={editing}
          deleting={deleting}
          loading={loading}
          submitForm={submitForm}
          canSubmit={canSubmitAfterValidation}
          onDelete={monitoredOnDelete}
        />
      </ScrollView>
    </>
  );
};

const usePopulateFields = ({
  address,
  visible,
  placeId,
  isAddressTouched,
  editing,
  setAddressType,
  setIsValidStreet,
  setForm,
}: PopulateFieldsParams) => {
  useEffect(() => {
    if (!visible) return;

    const homeOrWork = [ADDRESS_TYPE.HOME, ADDRESS_TYPE.WORK];
    const hasAddressName = Boolean(address?.name);
    const isAddressHomeOrWork = homeOrWork.includes(address?.name ?? '');
    const isCustom = hasAddressName && !isAddressHomeOrWork;
    const editingAddressType = isCustom ? ADDRESS_TYPE.CUSTOM : address?.name;

    setAddressType(editing ? editingAddressType ?? '' : '');
    setIsValidStreet(
      Boolean(placeId) || (!isAddressTouched && Boolean(address?.id)),
    );
    setForm({
      name: address?.name ?? defaultAddressForm.name,
      street: address?.street ?? defaultAddressForm.street,
      secondaryStreet:
        address?.secondaryStreet ?? defaultAddressForm.secondaryStreet,
      city: address?.city ?? defaultAddressForm.city,
      zipCode: address?.zipCode ?? defaultAddressForm.zipCode,
      state: address?.state ?? defaultAddressForm.state,
      country: address?.country ?? defaultAddressForm.country,
      googlePlaceId: address?.googlePlaceId ?? defaultAddressForm.googlePlaceId,
      latitude: address?.latitude ?? defaultAddressForm.latitude,
      longitude: address?.longitude ?? defaultAddressForm.longitude,
      notes: address?.notes ?? defaultAddressForm.notes,
      deliveryPreference:
        address?.deliveryPreference ?? defaultAddressForm.deliveryPreference,
    });
  }, [
    address,
    address?.id,
    address?.city,
    address?.country,
    address?.deliveryPreference,
    address?.googlePlaceId,
    address?.latitude,
    address?.longitude,
    address?.name,
    address?.notes,
    address?.secondaryStreet,
    address?.state,
    address?.street,
    address?.zipCode,
    isAddressTouched,
    placeId,
    editing,
    setForm,
    setAddressType,
    setIsValidStreet,
    visible,
  ]);
};

// Checks if the address form was changed at all.
const wasAddressChanged = (
  first: AddressFormType,
  second: AddressFormType,
): boolean => {
  if (first.name !== second.name) return true;

  if (first.street !== second.street) return true;

  if (first.secondaryStreet !== second.secondaryStreet) return true;

  if (first.zipCode !== second.zipCode) return true;

  if (first.city !== second.city) return true;

  if (first.state !== second.state) return true;

  if (first.country !== second.country) return true;

  if (first.deliveryPreference !== second.deliveryPreference) return true;

  if ((first.notes ?? '') !== (second.notes ?? '')) return true;

  return false;
};

const useCustomNameNotice = (
  isNameDuplicate?: boolean,
  isNameMissing?: boolean,
) => {
  const { t } = useLocalizationContext();

  if (isNameDuplicate) {
    return t('account.address.name-duplicate');
  }

  if (isNameMissing) {
    return t('account.addresses.required');
  }

  return '';
};

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
  },
  headerPadding: {
    paddingTop: theme.spacing.large,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  body: {
    paddingBottom: theme.spacing.large,
    paddingHorizontal: theme.spacing.large,
  },
  content: {
    width: '100%',
  },
});
